import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Card, Tag, Switch, Tooltip, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import authService from '../utils/auth';
import './SalespersonManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 权限模块配置
const PERMISSION_LABELS = {
  tours: '出团管理',
  salespersons: '员工管理',
  tourists: '游客管理',
  'ocr-logs': '系统设置',
  notice: '资源管理',
  finance: '财务管理'
};

// 提示：此页面原名"销售员工管理"，现改为"员工管理"，但代码中的变量名保持不变

// 默认权限配置
const DEFAULT_PERMISSIONS = {
  admin: ['tours', 'salespersons', 'tourists', 'ocr-logs', 'notice', 'finance'],
  operations: ['tours', 'tourists', 'notice'],
  salesperson: [],
  guide: [],
  finance: ['finance']
};

function SalespersonManagement() {
  const [salespersons, setSalespersons] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);
  const [form] = Form.useForm();
  const [stores, setStores] = useState([]);

  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3060/api'}/stores`, { headers: authService.getAuthHeaders() })
      .then((res) => setStores((res.data?.data || []).filter(s => s.isActive)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // 只有在已登录时才获取数据
    if (authService.isAuthenticated()) {
      fetchSalespersons();
    }
  }, []);

  const fetchSalespersons = async () => {
    // 再次检查认证状态
    if (!authService.isAuthenticated()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/salespersons`, {
        headers: authService.getAuthHeaders()
      });
      setSalespersons(response.data.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        // 401/403错误不显示消息，让路由保护处理
        return;
      }
      message.error('获取员工列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 把表单的 ocrEnabled 反转回后端字段 ocrDisabled
  const normalizeOcrField = (values) => {
    const { ocrEnabled, ...rest } = values;
    return { ...rest, ocrDisabled: ocrEnabled === false };
  };

  const handleUpdateSalesperson = async (values) => {
    try {
      await axios.put(`${API_BASE}/salespersons/${selectedSalesperson.id}`, normalizeOcrField(values), {
        headers: authService.getAuthHeaders()
      });
      message.success('更新成功');
      setModalVisible(false);
      form.resetFields();
      setSelectedSalesperson(null);
      fetchSalespersons();
    } catch (error) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  // 获取用户有效权限
  const getEffectivePermissions = (record) => {
    if (record.role === 'salesperson' || record.role === 'guide') {
      return ['自己的数据'];
    }
    return record.permissions || DEFAULT_PERMISSIONS[record.role] || [];
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 100,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 160,
      ellipsis: true,
      render: (email) => email || '-',
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || '-',
    },
    {
      title: '所属门店',
      dataIndex: 'store',
      key: 'store',
      width: 120,
      render: (store) => store?.name || '-',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 80,
      render: (role) => {
        const roleMap = {
          admin: { text: '管理员', color: 'red' },
          operations: { text: '运营', color: 'orange' },
          salesperson: { text: '万国销售', color: 'blue' },
          guide: { text: '导游', color: 'green' },
          finance: { text: '财务', color: 'gold' }
        };
        const config = roleMap[role] || { text: role, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '权限',
      key: 'permissions',
      width: 280,
      render: (_, record) => {
        const perms = getEffectivePermissions(record);
        if (record.role === 'salesperson' || record.role === 'guide') {
          return <Tag color="default">仅自己数据</Tag>;
        }
        return (
          <Space size={[0, 4]} wrap>
            {perms.map(p => (
              <Tag key={p} color="blue" style={{ margin: '2px' }}>
                {PERMISSION_LABELS[p] || p}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (isActive) => <Tag color={isActive ? 'green' : 'default'}>{isActive ? '启用' : '停用'}</Tag>,
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 140,
      render: (date) => date ? moment(date).format('MM-DD HH:mm') : '未登录',
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedSalesperson(record);
                form.setFieldsValue({
                  name: record.name,
                  username: record.username,
                  email: record.email,
                  phone: record.phone,
                  wechat: record.wechat,
                  storeId: record.storeId || undefined,
                  bankStoreId: record.bankStoreId || undefined,
                  ocrEnabled: !record.ocrDisabled
                });
                setModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="salesperson-management">
      <Card title="员工管理" className="management-card">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder="按姓名 / 用户名搜索"
            style={{ width: 260 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(val) => setSearchText(val)}
          />
        </Space>

        <Table
          columns={columns}
          dataSource={(() => {
            const kw = searchText.trim().toLowerCase();
            if (!kw) return salespersons;
            return salespersons.filter(s =>
              (s.name && s.name.toLowerCase().includes(kw)) ||
              (s.username && s.username.toLowerCase().includes(kw))
            );
          })()}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1220 }}
          size="small"
        />
      </Card>

      {/* 创建/编辑员工弹窗 */}
      <Modal
        title="编辑员工"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedSalesperson(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSalesperson}
        >
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 50, message: '用户名最多50个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input placeholder="请输入用户名（登录使用）" disabled={selectedSalesperson} />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱地址' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱地址" />
          </Form.Item>

          <Form.Item name="phone" label="电话">
            <Input placeholder="请输入电话号码（选填）" />
          </Form.Item>

          <Form.Item name="wechat" label="微信号">
            <Input placeholder="请输入微信号（选填）" />
          </Form.Item>

          {/* 客户端展示字段（万国会员客户端销售名片浮层使用） */}
          <Form.Item name="employeeCode" label="工号" tooltip="对外展示的工号，如 88027">
            <Input placeholder="如 88027（选填）" />
          </Form.Item>

          <Form.Item name="phoneDirect" label="直拨电话" tooltip="客户在万国会员看到并直接拨打的号码">
            <Input placeholder="如 +64 21 xxx xxxx（选填，留空则用上方电话）" />
          </Form.Item>

          <Form.Item name="bio" label="销售简介" tooltip='展示给客户看，如："已带团 200+"'>
            <Input placeholder='如："已带团 200+ 客户"（选填）' maxLength={120} />
          </Form.Item>

          <Form.Item name="wxKfId" label="企业微信客服 ID" tooltip="开通企业微信微信客服后获取的 kf_id，可让客户一键唤起会话">
            <Input placeholder="如 wkAgL6CgAAxxxxxx（选填）" />
          </Form.Item>

          <Form.Item name="avatarUrl" label="头像 URL" tooltip="可贴外网图片 URL 或上传到本地后的路径">
            <Input placeholder="https://... （选填）" />
          </Form.Item>

          <Form.Item name="qrCode" label="个人微信二维码 URL" tooltip="客户长按扫码加好友，wxKfId 优先使用">
            <Input placeholder="二维码图片 URL（选填）" />
          </Form.Item>


          <Form.Item name="storeId" label="所属门店">
            <Select placeholder="请选择门店" allowClear options={stores.map(s => ({ value: s.id, label: s.name }))} />
          </Form.Item>

          {/* 与所属门店解绑：有人不在门店坐班改成在家办公，门店要改，
              但发票上的收款账号不能跟着变 */}
          <Form.Item
            name="bankStoreId"
            label="开票收款账号"
            extra="发票上印的收款账号取自这个门店。留空则跟随所属门店；调动门店不会改变它。"
          >
            <Select
              placeholder="留空则跟随所属门店"
              allowClear
              options={stores.map(s => ({
                value: s.id,
                label: s.name,
              }))}
            />
          </Form.Item>



          <Form.Item
            name="ocrEnabled"
            label="OCR 自动识别护照"
            valuePropName="checked"
            initialValue={true}
            tooltip="开启时，该销售名下的游客上传护照后系统自动识别；关闭后只能手动输入"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedSalesperson(null);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
}

export default SalespersonManagement;